import { Fragment, useCallback, useEffect } from 'react';
import { Mosaic, MosaicBranch } from 'react-mosaic-component';
import { useNavigate } from 'react-router';
import Layout from './components/layout/Layout';
import IdleModal from './components/modal/IdleModal';
import BlottersModule from './components/modules/Blotters/BlottersModule';
import ChartModule from './components/modules/Chart/ChartModule';
import OrderBookModule from './components/modules/OrderBook/OrderBookModule';
import FeatureModule from './components/mosaic/FeatureModule';
import { addMarketFull } from './services/MarketService';
import { useAppDispatch, useAppSelector } from './state/hooks';
import { controlInitSubscriptions, selectCredentials } from './state/reducers/authSlice';
import { getBlotterItemsThunk, getBlotterTradesThunk } from './state/reducers/blotterSlice';
import { PairMap, fetchUserMarkets, setActivePair } from './state/reducers/marketPairSlice';
import { setIsResizing } from './state/reducers/mosaicSlice';
import { selectGeneralSettings } from './state/reducers/settingSlice';
import { setXplorSpotDesktopLayoutNode, setXplorSpotLaptopLayoutNode } from './state/reducers/xplorSpotLayoutSlice';
import { store } from './state/store';
import { useDisclosure } from './utils/hooks/useDisclosure';
import { useIdle } from './utils/hooks/useIdle';
import useScreenSizes from './utils/hooks/useScreenSizes';
import useXplorSpotLayout, { XplorSpotModules } from './utils/hooks/useXplorSpotLayout';
import { isCorrectLayout } from './utils/route';

const xplorspotEnabled = window.config?.layout?.xplorspot?.enabled;

export const initActivePair = () => {
    const state = store.getState();
    const dispatch = store.dispatch;

    const currentPair = state.marketPair.activePair?.netdania;
    const pairs = state.marketPair.pairs;
    if (!currentPair || !pairs.find((pair) => pair.netdania === currentPair)) {
        const primary = pairs.find((pair) => pair.netdania === 'BTC/USD');
        const secondary = pairs.find((pair) => pair.netdania === 'EUR/USD');
        const activePair = primary || secondary || pairs[0];
        dispatch(setActivePair(activePair));
        return activePair;
    } else {
        dispatch(setActivePair(state.marketPair.activePair as PairMap));
        return state.marketPair.activePair;
    }
};

function XplorSpot({ prevPath }: { prevPath: string | null }) {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const settings = useAppSelector(selectGeneralSettings);
    const credentials = useAppSelector(selectCredentials);

    const idle = useIdle(settings.idleTimeout.value);
    const idleDisclosure = useDisclosure(false);

    const screen = useScreenSizes();
    const { layout, default_layout, config } = useXplorSpotLayout(screen);

    const renderTile = useCallback(
        (id: XplorSpotModules, path: MosaicBranch[]) => {
            const props = {
                path,
                screen,
                tradingModule: config[id],
                orderBookModule: config[id]
            };
            switch (id) {
                case 'Chart':
                    return <ChartModule {...props} />;
                case 'Order Book':
                    return <OrderBookModule {...props} />;
                case 'Blotter':
                    return <BlottersModule {...props} />;
                default:
                    return <FeatureModule {...props} />;
            }
        },
        [screen, config]
    );

    useEffect(() => {
        if (idle) idleDisclosure[1].open();
    }, [idle]);

    useEffect(() => {
        if (!credentials) {
            navigate('/login');
        } else {
            if (isCorrectLayout('xplorspot')) {
                if (xplorspotEnabled) {
                    // Fire initial requests
                    dispatch(controlInitSubscriptions());
                    dispatch(fetchUserMarkets()).then(() => {
                        const activePair = initActivePair() as PairMap;
                        addMarketFull(activePair?.celer);
                        dispatch(getBlotterItemsThunk()).then(() => dispatch(getBlotterTradesThunk()));
                    });
                } else {
                    navigate('/lite');
                }
            } else {
                navigate('/trader');
            }
        }
    }, []);

    return credentials ? (
        <Fragment>
            <Layout prevPath={prevPath}>
                <Mosaic<XplorSpotModules>
                    renderTile={renderTile}
                    value={layout}
                    initialValue={layout || default_layout}
                    onChange={(node) => {
                        if (screen.laptop) dispatch(setXplorSpotLaptopLayoutNode(node));
                        if (screen.desktop) dispatch(setXplorSpotDesktopLayoutNode(node));
                        dispatch(setIsResizing(true));
                    }}
                    onRelease={() => dispatch(setIsResizing(false))}
                    resize={{ minimumPaneSizePercentage: 5 }}
                />
                <IdleModal opened={idleDisclosure[0]} handlers={idleDisclosure[1]} />
            </Layout>
        </Fragment>
    ) : null;
}

export default XplorSpot;
